import React, { useState, useRef, useEffect } from "react";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";
import { cn } from "../../../utils/cn";

const BatchCreationModal = ({ isOpen, onClose, onCreateBatch }) => {
  const [files, setFiles] = useState([]);
  const [batchName, setBatchName] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Generate default batch name based on current date
      const now = new Date();
      const dateStr = now?.toISOString()?.split("T")?.[0];
      setBatchName(
        `Batch_${dateStr}_${now?.getHours()}${String(
          now?.getMinutes(),
        )?.padStart(2, "0")}`,
      );
    }
  }, [isOpen]);

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === "dragenter" || e?.type === "dragover") {
      setDragActive(true);
    } else if (e?.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(e?.dataTransfer?.files || []);
    const pdfFiles = droppedFiles?.filter(
      (file) => file?.type === "application/pdf",
    );

    if (pdfFiles?.length !== droppedFiles?.length) {
      alert("Please only upload PDF files.");
      return;
    }

    setFiles((prev) => [
      ...prev,
      ...pdfFiles?.map((file) => {
        const timestamp = Date.now();
        const fileExtension = file?.name?.split(".")?.pop();
        const fileNameWithoutExt = file?.name?.replace(`.${fileExtension}`, "");
        const uniqueFileName = `${fileNameWithoutExt}_${timestamp}.${fileExtension}`;
        return {
          file,
          id: Math.random()?.toString(36)?.substr(2, 9),
          name: uniqueFileName,
          size: file?.size,
          status: "pending",
          timestamp,
        };
      }),
    ]);
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e?.target?.files || []);
    setFiles((prev) => [
      ...prev,
      ...selectedFiles?.map((file) => {
        const timestamp = Date.now();
        const fileExtension = file?.name?.split(".").pop();
        const fileNameWithoutExt = file?.name?.replace(`.${fileExtension}`, "");
        const uniqueFileName = `${fileNameWithoutExt}_${timestamp}.${fileExtension}`;
        return {
          file,
          id: Math.random()?.toString(36)?.substr(2, 9),
          name: uniqueFileName,
          size: file?.size,
          status: "pending",
          timestamp,
        };
      }),
    ]);
  };

  const removeFile = (fileId) => {
    setFiles((prev) => prev?.filter((f) => f?.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + " " + sizes?.[i];
  };

  const handleCreateBatch = async () => {
    if (!batchName?.trim()) {
      alert("Please enter a batch name.");
      return;
    }

    if (files?.length === 0) {
      alert("Please select at least one PDF file.");
      return;
    }

    setIsUploading(true);

    try {
      // Simulate file processing
      // await new Promise((resolve) => setTimeout(resolve, 2000));
      // console.log(files);
      const batchId = `BATCH-${Date.now()}`;
      const structuredFiles = files?.map((fileData) => ({
        file: fileData?.file,
        filename: fileData?.name,
        contentType: fileData?.file?.type || "application/pdf",
        size: fileData?.size,
      }));
      const invoices = files?.map((fileData, index) => ({
        id: `INV-2025-${String(Date.now() + index)?.slice(-3)}`,
        fileName: fileData?.name,
        uploadTime: new Date(),
        status: "processing",
        currentStep: 1,
        totalSteps: 5,
        errorMessage: null,
        batchId: batchId,
        steps: [
          { name: "File Conversion", status: "processing", duration: null },
          { name: "Schema Validation", status: "pending", duration: null },
          { name: "VAT/CRN Checks", status: "pending", duration: null },
          { name: "AI Anomaly Detection", status: "pending", duration: null },
          { name: "ZATCA Clearance", status: "pending", duration: null },
        ],
      }));

      onCreateBatch?.(invoices, {
        id: batchId,
        name: batchName,
        fileCount: files?.length,
        files: structuredFiles,
      });

      // Reset form
      setFiles([]);
      setBatchName("");
      setIsUploading(false);
      onClose?.();
    } catch (error) {
      console.error("Error creating batch:", error);
      alert("Error creating batch. Please try again.");
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 border border-border rounded-lg shadow-xl w-full max-w-4xl max-h-[96vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-lg">
              <Icon name="Layers" size={20} color="white" strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                Create New Batch
              </h2>
              <p className="text-sm text-muted-foreground">
                Upload PDF invoices for batch processing
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            disabled={isUploading}
            iconName="X"
          />
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 max-h-[calc(90vh-140px)] overflow-y-auto">
          {/* Batch Name Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Batch Name <span className="text-error">*</span>
            </label>
            <input
              type="text"
              value={batchName}
              onChange={(e) => setBatchName(e?.target?.value)}
              placeholder="Enter batch name..."
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              disabled={isUploading}
            />
          </div>

          {/* File Upload Area */}
          <div className="space-y-4">
            <label className="text-sm font-medium text-foreground">
              PDF Files <span className="text-error">*</span>
            </label>

            <div
              className={cn(
                "border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200",
                dragActive
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50 hover:bg-muted/50",
                isUploading && "opacity-50 pointer-events-none",
              )}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef?.current?.click()}
            >
              <div className="space-y-4">
                <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mx-auto">
                  <Icon
                    name="Upload"
                    size={24}
                    color="var(--color-primary)"
                    strokeWidth={2}
                  />
                </div>
                <div>
                  <p className="text-lg font-medium text-foreground">
                    Drop PDF files here
                  </p>
                  <p className="text-sm text-muted-foreground">
                    or{" "}
                    <span className="text-primary font-medium cursor-pointer">
                      click to browse
                    </span>
                  </p>
                </div>
                <p className="text-xs text-muted-foreground">
                  Supports: PDF files up to 10MB each
                </p>
              </div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf"
              onChange={handleFileSelect}
              className="hidden"
              disabled={isUploading}
            />
          </div>

          {/* File List */}
          {files?.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-foreground">
                  Selected Files ({files?.length})
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFiles([])}
                  disabled={isUploading}
                  iconName="Trash2"
                  className="text-muted-foreground hover:text-error"
                >
                  Clear All
                </Button>
              </div>

              <div className="max-h-48 overflow-y-auto space-y-2 border border-border rounded-md p-3 bg-background">
                {files?.map((fileData) => (
                  <div
                    key={fileData?.id}
                    className="flex items-center justify-between py-2 px-3 bg-muted border border-border rounded-md"
                  >
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <div className="flex items-center justify-center w-8 h-8 bg-error/10 rounded">
                        <Icon
                          name="FileText"
                          size={16}
                          color="var(--color-error)"
                          strokeWidth={2}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground truncate">
                          {fileData?.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(fileData?.size)}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeFile(fileData?.id)}
                      disabled={isUploading}
                      iconName="X"
                      className="text-muted-foreground hover:text-error flex-shrink-0"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/20">
          <div className="text-sm text-muted-foreground">
            {files?.length > 0 &&
              `${files?.length} file${files?.length !== 1 ? "s" : ""} selected`}
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={onClose} disabled={isUploading}>
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleCreateBatch}
              loading={isUploading}
              iconName="Plus"
              iconPosition="left"
              disabled={!batchName?.trim() || files?.length === 0}
            >
              {isUploading ? "Creating Batch..." : "Create Batch"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BatchCreationModal;
